import { Component, createRef } from 'react';
import styles from './editor.module.scss';
import { StageComponent, ViewMode, Preview, StageThumb } from './stage/stage.component';
import { InspectorComponent, ToolTabs, SaveFormat, OutputInfo, LOSSY } from './inspector/inspector.component';
import { EditState, Tool, Size, initialState, outputSize, orientedSize, resetAll, resetTool, outputFilename, isApplied, currentIndex } from './model/edit-stack';
import { renderOutput, renderPreview, estimateBytes, formatBytes, scaleTo, MipCache } from './model/render';
import { applyMasterToItem, CropPolicy, BATCH_LIMIT } from './model/batch';
import { zipEntries } from './batch/zip';
import type { ImageFormat, DecodedImage } from './codecs/types';
import type { LandingSpec } from './routing';
import { formatLabel } from './routing';

export interface EditorItem {
    id: string;
    file: File;
    decoded: DecodedImage | null;
    error: string | null;
    state: EditState | null;
    thumb: HTMLCanvasElement | null;
    /** Undo / redo history for this photo: one entry per user action (a whole drag is one). */
    past: EditState[];
    future: EditState[];
    /** Downscaled copies of the source, built once per scale and shared by previews and the export. */
    mips: MipCache;
}

export interface EditorProps {
    files: File[];
    landing: LandingSpec;
    onChangeFiles: () => void;      // "Change" → back to the empty state
    onAddMore: () => void;          // opens the picker again (root keeps the list)
    onTelemetry?: (event: string, attrs?: Record<string, unknown>) => void;
}

interface EditorState {
    items: EditorItem[];
    selected: number;
    tool: Tool;
    view: ViewMode;
    zoom: number;
    format: SaveFormat;
    quality: number;
    cropPolicy: CropPolicy;
    preview: Preview | null;
    sourcePreview: Preview | null;
    basePreview: Preview | null;   // Crop mode: the image the ops before the current crop produced (additive model)
    busy: string | null;
    exporting: { done: number; total: number; label: string } | null;
    fatal: string | null;
    fullscreen: boolean;
    fsSupported: boolean;      // set on mount; iOS Safari has no element fullscreen → no ⛶ (UX review U1)
    boxHeight: number | null;  // phone canvas box height, chosen once per photo (UX review M4)
    canShare: boolean;         // navigator.share with files (phones): "Share…" beside Download (UX review U7)
    angleDragging: boolean;    // fine-angle slider held → thirds grid over the photo (UX review N3)
}

const DESKTOP = () => typeof window !== 'undefined' && window.innerWidth >= 901;
const SNAP_TOL = 20; // px from top/bottom within which the editor snaps to fill the screen
const TOOL_LIST: Tool[] = ['resize', 'crop', 'rotate', 'flip'];

// Cap on a preview canvas's longer edge: previews render at the screen's device resolution for the
// current zoom, so this only bounds memory (a 3× phone box ≈ 1,200 px; a 2× desktop box ≈ 2,700 px).
const PREVIEW_EDGE = () => (typeof window !== 'undefined' && window.innerWidth <= 900 ? 1600 : 3200);

/**
 * The workspace: owns the batch, the per-item edit states, previews and export. The selected item's
 * state is the "master"; in a batch the other items get a translation of it at export time
 * (model/batch.ts). Decoding is lazy per item; the first file decodes immediately.
 */
export class EditorComponent extends Component<EditorProps, EditorState> {
    private codecs: typeof import('./codecs') | null = null;
    private renderToken = 0;
    private previewTimer: ReturnType<typeof setTimeout> | null = null;
    private live = true;
    private editorRef = createRef<HTMLDivElement>();

    constructor(props: EditorProps) {
        super(props);
        this.state = {
            items: props.files.slice(0, BATCH_LIMIT).map((f, i) => ({ id: `${Date.now()}-${i}-${f.name}`, file: f, decoded: null, error: null, state: null, thumb: null, past: [], future: [], mips: new Map() })),
            selected: 0, tool: props.landing.tool, view: 'fit', zoom: 1,
            format: props.landing.defaultFormat === 'original' ? 'original' : props.landing.defaultFormat,
            quality: 85, cropPolicy: 'centre-aspect', preview: null, sourcePreview: null, basePreview: null, busy: 'Opening…', exporting: null, fatal: null, fullscreen: false,
            fsSupported: false, boxHeight: null, canShare: false, angleDragging: false,
        };
    }

    async componentDidMount() {
        if (typeof window !== 'undefined') {
            document.addEventListener('fullscreenchange', this.onFsChange);
            // Listen in the capture phase on both window and document so we catch the settle no matter
            // which element is the page's scroll container (this layout scrolls <body>, not the window).
            window.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
            document.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
            window.addEventListener('keydown', this.onKey);
            window.addEventListener('resize', this.onResize);
            // set after hydration so the SSR markup (no ⛶) matches the first client render
            if (document.fullscreenEnabled) this.setState({ fsSupported: true });
            this.watchAds();
            try { const n: any = navigator; if (n.canShare && n.canShare({ files: [new File([''], 'x.png', { type: 'image/png' })] })) this.setState({ canShare: true }); } catch { /* no Web Share */ }
        }
        this.codecs = await import('./codecs');
        await this.ensureDecoded(0);
        this.schedulePreview(true);
        // Once the image is actually in, centre the editor so it opens as its own full-screen workspace
        // (owner request 2026-09-06). After decode + a frame the layout is settled, so this holds — an
        // earlier attempt fired at mount and got undone.
        if (typeof window !== 'undefined') requestAnimationFrame(() => { if (this.live && DESKTOP() && !document.fullscreenElement) this.snapEditor('start'); });
        // decode the rest in the background for thumbnails
        for (let i = 1; i < this.state.items.length; i++) { if (!this.live) return; await this.ensureDecoded(i); }
    }
    componentWillUnmount() {
        this.live = false; if (this.previewTimer) clearTimeout(this.previewTimer); if (this.settleTimer) clearTimeout(this.settleTimer);
        if (typeof window !== 'undefined') {
            cancelAnimationFrame(this.animId); if (this.resizeTimer) clearTimeout(this.resizeTimer);
            this.adTimers.forEach(clearTimeout); this.adTimers = []; this.adObserver?.disconnect();
            window.removeEventListener('keydown', this.onKey); window.removeEventListener('resize', this.onResize);
            document.removeEventListener('fullscreenchange', this.onFsChange);
            window.removeEventListener('scroll', this.onScroll, { capture: true } as any);
            document.removeEventListener('scroll', this.onScroll, { capture: true } as any);
        }
    }

    // Distraction-free full-window editor. Layout is driven by :fullscreen in the stylesheet; the
    // stage's ResizeObserver re-fits the preview when the element resizes.
    private toggleFullscreen = () => {
        const el = this.editorRef.current; if (!el) return;
        if (document.fullscreenElement) document.exitFullscreen?.();
        else el.requestFullscreen?.().catch(() => { /* denied by the browser; ignore */ });
    };
    private onFsChange = () => { if (this.live) this.setState({ fullscreen: !!document.fullscreenElement }); };

    // Proximity snap: when a scroll SETTLES (debounced — no dependency on the flaky scrollend event)
    // with the editor almost filling the viewport (within SNAP_TOL of the top or bottom edge), glide it
    // into a clean fill. Desktop only, never in fullscreen, and never while our own snap is animating.
    private settleTimer: ReturnType<typeof setTimeout> | null = null;
    private snapGuardUntil = 0;
    private onScroll = () => {
        if (!DESKTOP()) return;
        if (this.settleTimer) clearTimeout(this.settleTimer);
        this.settleTimer = setTimeout(this.maybeSnap, 140);
    };
    private maybeSnap = () => {
        const el = this.editorRef.current;
        if (!el || !DESKTOP() || this.state.fullscreen || document.fullscreenElement || Date.now() < this.snapGuardUntil) return;
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight;
        if (r.height > vh + 4) return; // taller than the screen — nothing to snap to
        if (r.top !== 0 && Math.abs(r.top) <= SNAP_TOL) this.snapEditor('start');
        else if (r.bottom !== vh && Math.abs(r.bottom - vh) <= SNAP_TOL) this.snapEditor('end');
    };

    // Scroll the editor to fill the viewport (top or bottom aligned). Native smooth scrollIntoView is a
    // no-op on this page's <body> scroller, so animate scrollTop ourselves — works on any scroller.
    private animId = 0;
    private getScroller(): HTMLElement {
        let el = this.editorRef.current?.parentElement as HTMLElement | null;
        while (el) { const oy = getComputedStyle(el).overflowY; if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight + 2) return el; el = el.parentElement; }
        const b = document.body; if (b.scrollHeight > b.clientHeight + 2) return b;
        return (document.scrollingElement as HTMLElement) || document.documentElement;
    }
    private snapEditor(block: 'start' | 'end') {
        const el = this.editorRef.current; if (!el) return;
        this.snapGuardUntil = Date.now() + 700;
        const sc = this.getScroller();
        const r = el.getBoundingClientRect();
        const target = sc.scrollTop + (block === 'start' ? r.top : r.bottom - window.innerHeight);
        this.smoothScrollTo(sc, target);
    }
    private smoothScrollTo(sc: HTMLElement, target: number) {
        cancelAnimationFrame(this.animId);
        const start = sc.scrollTop, dist = target - start, dur = 280, t0 = performance.now();
        if (Math.abs(dist) < 1) return;
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) { sc.scrollTop = target; return; }  // no glide for reduced motion (N6)
        const step = (now: number) => {
            const p = Math.min(1, (now - t0) / dur), e = 1 - Math.pow(1 - p, 3); // easeOutCubic
            sc.scrollTop = start + dist * e;
            if (p < 1 && this.live) this.animId = requestAnimationFrame(step);
        };
        this.animId = requestAnimationFrame(step);
    }

    componentDidUpdate(_: EditorProps, prev: EditorState) {
        if (prev.selected !== this.state.selected) { this.ensureDecoded(this.state.selected).then(() => { this.fitBox(); this.schedulePreview(true); }); }
        else if (prev.tool !== this.state.tool && this.state.tool === 'crop') this.schedulePreview(true);   // build the crop base
        else if (prev.zoom !== this.state.zoom || prev.view !== this.state.view) this.schedulePreview();    // re-render at the new screen resolution
    }

    // ------------------------------------------------------------ items
    private get current(): EditorItem { return this.state.items[this.state.selected]; }

    private async ensureDecoded(i: number) {
        const it = this.state.items[i]; if (!it || it.decoded || it.error || !this.codecs) return;
        try {
            const decoded = await this.codecs.decodeImage(it.file);
            const state = initialState({ width: decoded.width, height: decoded.height });
            const thumb = await scaleTo(decoded.source, this.fitThumb({ width: decoded.width, height: decoded.height }));
            this.patchItem(i, { decoded, state, thumb });
            if (i === this.state.selected) this.fitBox({ width: decoded.width, height: decoded.height });
            this.props.onTelemetry?.('EDITOR_FILE_OPENED', { format: decoded.sourceFormat, width: decoded.width, height: decoded.height, bytes: it.file.size });
        } catch (e: any) {
            this.patchItem(i, { error: e?.message || 'This file could not be opened.' });
            this.props.onTelemetry?.('EDITOR_FILE_FAILED', { name: it.file.name.split('.').pop(), reason: String(e?.message || e).slice(0, 80) });
        }
    }
    private fitThumb(size: Size): Size { const k = 128 / Math.max(size.width, size.height); return { width: Math.max(1, Math.round(size.width * k)), height: Math.max(1, Math.round(size.height * k)) }; }
    private patchItem(i: number, patch: Partial<EditorItem>) {
        if (!this.live) return;
        this.setState(s => ({ items: s.items.map((it, j) => j === i ? { ...it, ...patch } : it) }));
    }

    // ------------------------------------------------------------ previews
    private schedulePreview(immediate = false) {
        if (this.previewTimer) clearTimeout(this.previewTimer);
        this.previewTimer = setTimeout(() => this.renderPreviews(), immediate ? 0 : 60);
    }
    private async renderPreviews() {
        const it = this.current; if (!it?.decoded || !it.state) { this.setState({ busy: it?.error ? null : 'Opening…' }); return; }
        const token = ++this.renderToken;
        try {
            const edge = PREVIEW_EDGE();
            const st = it.state;
            // Crop edits the image the ops BEFORE the current crop produced (additive model, 2026-09-06):
            // render that base as well while the Crop tool is open.
            const cropAt = currentIndex(st, 'crop'); const upTo = cropAt < 0 ? st.ops.length : cropAt;
            // Previews are drawn from the ORIGINAL at the screen's resolution for the current zoom (device
            // pixels), so a photo shrunk to 277 px never previews as a tiny canvas stretched across a
            // Retina screen (owner, 2026-09-06). `zoom` follows what the stage shows (Fit re-syncs it).
            const displayScale = Math.max(0.02, this.state.zoom || 1) * ((typeof window !== 'undefined' && window.devicePixelRatio) || 1);
            const opts = { maxEdge: edge, displayScale, mips: it.mips };
            const [p, sp, bp] = await Promise.all([
                renderPreview(it.decoded.source, st, opts),
                this.state.sourcePreview && this.state.sourcePreview.canvas.dataset.itemId === it.id ? Promise.resolve(this.state.sourcePreview) : renderPreview(it.decoded.source, initialState(st.source), opts),
                this.state.tool === 'crop' ? renderPreview(it.decoded.source, st, { ...opts, upTo }) : Promise.resolve(null),
            ]);
            if (token !== this.renderToken || !this.live) return;
            sp.canvas.dataset.itemId = it.id;
            this.setState({ preview: p, sourcePreview: sp, basePreview: bp, busy: null });
        } catch (e: any) {
            if (token === this.renderToken && this.live) this.setState({ busy: null, fatal: e?.message || 'The preview could not be drawn.' });
        }
    }

    // ------------------------------------------------------------ edits + history (undo / redo)
    // One history entry per user INTENT: a handle drag is one step (onDragging brackets it), and
    // rapid repeats — a slider, typing digits — coalesce within 400ms so undo steps back by action,
    // not by keystroke. Owner asked for undo/redo + Reset over the photo (2026-09-06).
    private dragging = false; private dragPushed = false; private lastPush = 0;
    private static readonly HISTORY_MAX = 60;
    private setEditState = (next: EditState) => {
        const i = this.state.selected; const now = Date.now();
        const push = !(this.dragging && this.dragPushed) && now - this.lastPush > 400;
        if (this.dragging) this.dragPushed = true;
        if (push) this.lastPush = now;
        this.setState(s => ({ items: s.items.map((it, j) => {
            if (j !== i) return it;
            const past = push && it.state ? [...it.past, it.state].slice(-EditorComponent.HISTORY_MAX) : it.past;
            return { ...it, state: next, past, future: push ? [] : it.future };
        }) }), () => this.schedulePreview());
    };
    private onDragging = (d: boolean) => { this.dragging = d; this.dragPushed = false; if (d) this.lastPush = 0; };
    private undo = () => this.stepHistory('undo');
    private redo = () => this.stepHistory('redo');
    private stepHistory(dir: 'undo' | 'redo') {
        const i = this.state.selected; this.lastPush = 0;
        this.setState(s => ({ items: s.items.map((it, j) => {
            if (j !== i || !it.state) return it;
            if (dir === 'undo') { if (!it.past.length) return it; return { ...it, state: it.past[it.past.length - 1], past: it.past.slice(0, -1), future: [it.state, ...it.future] }; }
            if (!it.future.length) return it; return { ...it, state: it.future[0], future: it.future.slice(1), past: [...it.past, it.state] };
        }) }), () => this.schedulePreview());
    }
    // Reset (all, or one tool) also returns the view to Fit: a zoom chosen for a small result would leave
    // the restored original spilling out of the box (owner, 2026-09-06).
    private resetAll = () => { const st = this.current?.state; if (st) { this.lastPush = 0; this.setEditState(resetAll(st)); this.setState({ view: 'fit' }); } };
    private resetToolFit = (t: Tool) => { const st = this.current?.state; if (st) { this.lastPush = 0; this.setEditState(resetTool(st, t)); this.setState({ view: 'fit' }); } };
    private onTool = (t: Tool) => { this.setState({ tool: t }); this.props.onTelemetry?.('EDITOR_TOOL', { tool: t }); };
    private onKey = (e: KeyboardEvent) => {
        const t = e.target as HTMLElement | null; if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
        if (!(e.ctrlKey || e.metaKey)) return;
        const k = e.key.toLowerCase();
        if (k === 'z') { e.preventDefault(); if (e.shiftKey) this.redo(); else this.undo(); }
        else if (k === 'y') { e.preventDefault(); this.redo(); }
    };

    /** Phone only: choose the canvas box height ONCE per photo from its aspect (34–56vh) and keep it
     *  fixed while editing — a flat 46vh left 44% of the box empty for landscape photos (UX review M4)
     *  while the controls sat below the fold. Desktop flexes to fill the screen instead. */
    // ------------------------------------------------------------ floating ads (AdSense auto ads)
    // The site loads adsbygoogle.js, and auto ads float side rails on wide screens and an anchor at the
    // bottom on phones. Converter pages sit in a 994px column so the rails land in the margins; this
    // editor is full-bleed, so on prod they covered the photo, the inspector and the Download sheet
    // (owner, 2026-09-07). Rather than give up the screen unconditionally, measure whatever is actually
    // floating and inset the editor by exactly that much — with no ad (ad blocker, no fill) the editor
    // stays full-bleed. Ads arrive late and move, so re-measure for a while and on DOM changes.
    private adTimers: ReturnType<typeof setTimeout>[] = [];
    private adObserver: MutationObserver | null = null;
    private adGutters = { l: 0, r: 0, b: 0 };
    private static readonly AD_SELECTOR = 'iframe[src*="googlesyndication"], iframe[src*="doubleclick"], iframe[id^="aswift"], iframe[id^="google_ads"], .google-auto-placed, ins.adsbygoogle';
    private measureAds = () => {
        const el = this.editorRef.current; if (!el || !this.live || typeof window === 'undefined') return;
        const r = el.getBoundingClientRect();
        const vw = window.innerWidth, vh = window.innerHeight;
        let l = 0, rt = 0, b = 0;
        document.querySelectorAll(EditorComponent.AD_SELECTOR).forEach(n => {
            // the floating box is the nearest fixed/sticky ancestor (Google wraps the iframe in one)
            let box: HTMLElement | null = n as HTMLElement;
            while (box && !/fixed|sticky/.test(getComputedStyle(box).position)) box = box.parentElement;
            if (!box) return;
            const a = box.getBoundingClientRect();
            if (a.width < 30 || a.height < 30) return;
            if (a.bottom <= r.top || a.top >= r.bottom || a.right <= r.left || a.left >= r.right) return;   // no overlap
            if (a.bottom > vh - 8 && a.width > vw * 0.5) { b = Math.max(b, Math.min(vh * 0.35, vh - a.top)); return; }   // bottom anchor
            if (a.left <= vw / 2) l = Math.max(l, Math.min(vw * 0.25, a.right - r.left));                                 // left rail
            else rt = Math.max(rt, Math.min(vw * 0.25, r.right - a.left));                                                // right rail
        });
        const g = { l: Math.round(Math.max(0, l)), r: Math.round(Math.max(0, rt)), b: Math.round(Math.max(0, b)) };
        if (g.l === this.adGutters.l && g.r === this.adGutters.r && g.b === this.adGutters.b) return;
        this.adGutters = g;
        el.style.setProperty('--ed-ad-l', `${g.l}px`); el.style.setProperty('--ed-ad-r', `${g.r}px`); el.style.setProperty('--ed-ad-b', `${g.b}px`);
        this.fitBox();
    };
    private watchAds() {
        if (typeof window === 'undefined') return;
        [300, 1200, 3000, 6000, 10000].forEach(t => this.adTimers.push(setTimeout(this.measureAds, t)));
        this.adObserver = new MutationObserver(() => { if (this.adTimers.length < 40) this.adTimers.push(setTimeout(this.measureAds, 250)); });
        this.adObserver.observe(document.body, { childList: true, subtree: false });
    }

    private resizeTimer: ReturnType<typeof setTimeout> | null = null;
    private fitBox = (size?: Size) => {
        if (typeof window === 'undefined' || !this.live) return;
        if (DESKTOP()) { if (this.state.boxHeight !== null) this.setState({ boxHeight: null }); return; }
        const d = size ?? this.current?.decoded; if (!d) return;
        // fitZoom leaves 36px of the box's width for the handles' touch zones (box padding 12px 18px)
        // fitZoom leaves 112px of the box (≈½ in each side) so a grip can be dragged OUTWARD (owner, 2026-09-06)
        const photoW = Math.max(80, (this.editorRef.current?.clientWidth || window.innerWidth) - 112);
        const imgH = photoW * (d.height / d.width);
        const h = Math.round(Math.min(0.6 * window.innerHeight, Math.max(0.36 * window.innerHeight, imgH + 112)));
        if (h !== this.state.boxHeight) this.setState({ boxHeight: h });
    };
    private onResize = () => { if (this.resizeTimer) clearTimeout(this.resizeTimer); this.resizeTimer = setTimeout(() => { this.fitBox(); this.measureAds(); }, 150); };

    // ------------------------------------------------------------ output info
    private effectiveFormat(): ImageFormat {
        const it = this.current; const src = it?.decoded?.sourceFormat ?? 'jpg';
        const wanted: ImageFormat = this.state.format === 'original' ? src : this.state.format;
        return this.codecs ? this.codecs.encodeFallback(wanted) : wanted;
    }
    private outputInfo(): OutputInfo {
        const it = this.current; const st = it?.state ?? initialState({ width: 1, height: 1 });
        const size = outputSize(st); const fmt = this.effectiveFormat();
        const wanted: ImageFormat | null = this.state.format === 'original' ? (it?.decoded?.sourceFormat ?? null) : this.state.format;
        const ext = this.codecs ? this.codecs.extensionFor(fmt) : fmt;
        const est = estimateBytes(size, fmt, this.state.quality, it?.decoded?.hasAlpha ?? false);
        const orig = it?.file.size ?? 0;
        // untouched + same format = the original file, byte for byte — say so instead of a scary "+1045%"
        if (it && this.isUntouched(st)) return { size, format: fmt, extension: ext, estimate: `${formatBytes(orig)} · unchanged`, delta: null, filename: it.file.name, note: null };
        const delta = orig ? { pct: Math.abs(Math.round(100 * (1 - est / orig))), up: est > orig } : null;
        let note: string | null = null;
        if (wanted && wanted !== fmt) note = `${formatLabel(wanted)} can't be written in the browser — saving as ${formatLabel(fmt)}.`;
        else if (fmt === 'ico' && Math.max(size.width, size.height) > 256) note = 'ICO files are limited to 256 px: the icon will be scaled to fit 256 × 256.';
        else if (it?.decoded?.animated && (fmt === 'gif' || fmt === 'png')) note = 'Animated input: only the first frame is edited and saved.';
        return { size, format: fmt, extension: ext, estimate: `≈ ${formatBytes(est)}`, delta, filename: outputFilename(it?.file.name ?? 'image', size, ext), note };
    }

    // ------------------------------------------------------------ export
    /** No edits and the same format → the original bytes ARE what the user wants (UX review D1: a
     *  28 KB JPG came back as 318 KB, "+1045%"). Also keeps an untouched GIF animated. Batches and
     *  format changes always go through the encoder. */
    private isUntouched(st: EditState): boolean {
        const src = this.current?.decoded?.sourceFormat;
        return this.state.items.length === 1 && !TOOL_LIST.some(t => isApplied(st, t)) && !!src && (this.state.format === 'original' || this.state.format === src);
    }
    /** Phones: the share sheet is how a photo reaches Photos / WhatsApp / Instagram (U7). Falls back
     *  to a download if the browser declines (e.g. the tap's activation expired during a slow encode). */
    private async shareFile(f: File) {
        try { await (navigator as any).share({ files: [f], title: f.name }); }
        catch (e: any) { if (e?.name !== 'AbortError') { const fs: any = await import('file-saver'); (fs.saveAs || fs.default?.saveAs || fs.default)(f, f.name); } }
    }
    private onExport = async (mode: 'single' | 'zip' | 'each' | 'share') => {
        if (!this.codecs || this.state.exporting) return;
        // file-saver ships CommonJS: the dynamic-import namespace exposes it only as `default`
        // (a function that also has `.saveAs`), not as a named `saveAs`. Destructuring `{ saveAs }`
        // yields undefined → "t is not a function" at call time (caught on QA 2026-09-06).
        const fs: any = await import('file-saver');
        const saveAs: (blob: Blob, name: string) => void = fs.saveAs || fs.default?.saveAs || fs.default;
        const items = this.state.items.filter(it => !it.error);
        const master = this.current.state!;
        const fmtWanted = this.state.format;
        const targets = mode === 'single' || mode === 'share' ? [this.current] : items;
        this.setState({ exporting: { done: 0, total: targets.length, label: 'Preparing…' } });
        this.props.onTelemetry?.('EDITOR_EXPORT_START', { mode, files: targets.length, format: fmtWanted, tools: (['resize', 'crop', 'rotate', 'flip'] as Tool[]).filter(t => isApplied(master, t)) });
        const entries: { name: string; blob: Blob }[] = [];
        try {
            if ((mode === 'single' || mode === 'share') && this.isUntouched(master)) {
                const f = this.current.file;
                if (mode === 'share') await this.shareFile(f); else saveAs(f, f.name);
                this.props.onTelemetry?.('EDITOR_EXPORT_DONE', { mode, files: 1, passthrough: true });
                return;
            }
            for (let i = 0; i < targets.length; i++) {
                const it = targets[i];
                await this.ensureDecoded(this.state.items.indexOf(it));
                const fresh = this.state.items[this.state.items.indexOf(it)];
                if (!fresh.decoded) continue;
                const st = it === this.current ? master : applyMasterToItem(master, { width: fresh.decoded.width, height: fresh.decoded.height }, this.state.cropPolicy);
                let canvas = await renderOutput(fresh.decoded.source, st, fresh.mips);   // one high-quality resample from the original
                const wanted: ImageFormat = fmtWanted === 'original' ? fresh.decoded.sourceFormat : fmtWanted;
                let fmt = this.codecs.encodeFallback(wanted);
                if (fmt === 'ico' && Math.max(canvas.width, canvas.height) > 256) { const k = 256 / Math.max(canvas.width, canvas.height); canvas = await scaleTo(canvas, { width: Math.max(1, Math.round(canvas.width * k)), height: Math.max(1, Math.round(canvas.height * k)) }); }
                const res = await this.codecs.encodeCanvas(canvas, fmt, { quality: this.state.quality });
                const name = outputFilename(fresh.file.name, { width: canvas.width, height: canvas.height }, res.extension);
                if (mode === 'each') saveAs(res.blob, name); else entries.push({ name, blob: res.blob });
                this.setState({ exporting: { done: i + 1, total: targets.length, label: mode === 'single' ? 'Preparing…' : 'Processing' } });
            }
            if (mode === 'single' && entries[0]) saveAs(entries[0].blob, entries[0].name);
            if (mode === 'share' && entries[0]) await this.shareFile(new File([entries[0].blob], entries[0].name, { type: entries[0].blob.type }));
            if (mode === 'zip') {
                this.setState({ exporting: { done: targets.length, total: targets.length, label: 'Zipping' } });
                const zip = await zipEntries(entries);
                saveAs(zip, `xconvert-${this.state.tool}-${entries.length}-files.zip`);
            }
            this.props.onTelemetry?.('EDITOR_EXPORT_DONE', { mode, files: entries.length || targets.length });
        } catch (e: any) {
            this.setState({ fatal: e?.message || 'The file could not be saved.' });
            this.props.onTelemetry?.('EDITOR_EXPORT_FAILED', { reason: String(e?.message || e).slice(0, 80) });
        } finally {
            this.setState({ exporting: null });
        }
    };

    // ------------------------------------------------------------ render
    render() {
        const { items, selected, tool, view, zoom, preview, sourcePreview, basePreview, busy, fatal } = this.state;
        const it = items[selected];
        const st = it?.state ?? initialState({ width: 1, height: 1 });
        const out = outputSize(st);
        const outputNote = it?.error ? it.error
            : items.length > 1 ? 'Preview shows the selected photo. Every file gets the same settings.'
            : view === 'actual' ? `Shown at actual size: ${out.width} × ${out.height} px.`
            : view === 'original' ? `Showing the untouched original (${st.source.width} × ${st.source.height} px).`
            : `Preview is scaled to fit. The file you download will be ${out.width} × ${out.height} px.`;
        const thumbs: StageThumb[] = items.map(x => ({ id: x.id, name: x.file.name, size: x.decoded ? { width: x.decoded.width, height: x.decoded.height } : { width: 0, height: 0 }, thumb: x.thumb, error: x.error ?? undefined }));
        const fileInfo = it?.decoded ? `${it.decoded.width} × ${it.decoded.height} · ${formatBytes(it.file.size)}` : it?.error ? 'could not open' : 'opening…';
        return <div className={styles.root}>
            {fatal && <div className={styles.errorNote} role="alert">{fatal} <button type="button" className={styles.link} onClick={() => this.setState({ fatal: null })}>Dismiss</button></div>}
            <div className={styles.editor} ref={this.editorRef}>
                <ToolTabs state={st} tool={tool} onTool={this.onTool} />
                <StageComponent
                    state={st} tool={tool} view={view} zoom={zoom}
                    preview={preview} sourcePreview={sourcePreview} basePreview={basePreview}
                    onFullscreen={this.state.fsSupported ? this.toggleFullscreen : undefined} isFullscreen={this.state.fullscreen}
                    history={{ canUndo: (it?.past.length ?? 0) > 0, canRedo: (it?.future.length ?? 0) > 0, edited: TOOL_LIST.some(t => isApplied(st, t)) }}
                    onUndo={this.undo} onRedo={this.redo} onResetAll={this.resetAll} onDragging={this.onDragging} boxHeight={this.state.boxHeight} showGrid={this.state.angleDragging}
                    onZoom={(z, v) => this.setState({ zoom: z, view: v })}
                    fileName={items.length > 1 ? `${items.length} photos` : (it?.file.name ?? '')}
                    fileInfo={items.length > 1 ? `${formatBytes(items.reduce((a, x) => a + x.file.size, 0))} · settings apply to all` : fileInfo}
                    changeLabel={items.length > 1 ? 'Add more' : 'Change'}
                    onChangeFile={items.length > 1 ? this.props.onAddMore : this.props.onChangeFiles}
                    busy={busy} onState={this.setEditState} outputNote={outputNote}
                    batch={items.length > 1 ? { items: thumbs, selected, onSelect: i => this.setState({ selected: i, preview: null }), onAddMore: this.props.onAddMore, remaining: BATCH_LIMIT - items.length } : null}
                />
                <InspectorComponent
                    state={st} tool={tool} onTool={this.onTool}
                    onState={this.setEditState} onResetAll={this.resetAll} onResetTool={this.resetToolFit}
                    sourceFormat={it?.decoded?.sourceFormat ?? 'unknown'} originalBytes={it?.file.size ?? 0}
                    format={this.state.format} onFormat={f => this.setState({ format: f })}
                    quality={this.state.quality} onQuality={q => this.setState({ quality: q })}
                    output={this.outputInfo()} batchCount={items.length}
                    cropPolicy={this.state.cropPolicy} onCropPolicy={p => this.setState({ cropPolicy: p })}
                    onAngleDragging={d => { if (d !== this.state.angleDragging) this.setState({ angleDragging: d }); }}
                    exporting={this.state.exporting} onExport={this.onExport} canShare={this.state.canShare}
                />
            </div>
        </div>;
    }
}

export { LOSSY, orientedSize };
